/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly coupled to the Product template.
 *
 * @namespace product
 */
import { formatMoney } from '@shopify/theme-currency';
import { ProductForm } from '@shopify/theme-product-form';
import { register } from '@shopify/theme-sections';
import Flickity from 'flickity';
import { FormDataJson } from 'form-data-json-convert';
import 'flickity-imagesloaded';
import 'flickity-fullscreen';
import 'flickity-as-nav-for';
import Dialog from 'a11y-dialog-component';
import * as PlattarARAdapter from '@plattar/plattar-ar-adapter';
import recentlyViewedProducts from '../components/recently-viewed-products';
import modelViewerLoader from '../components/model-viewer-loader';
import { pushToCartEvent } from '../ga4-events-scripts';

const selectors = {
  formElement: '[data-product-form]',
  addToCart: '[data-add-to-cart]',
  addToCartSticky: '[data-add-to-cart-sticky]',
  addPairAndSave: '[data-add-pair-and-save]',
  backInStock: '.klaviyo-bis-trigger',
  comparePrice: '[data-product-compare-at-price]',
  priceWrapper: '[data-price-wrapper]',
  productJson: '[data-product-json]',
  modelsJson: '[data-product-models-json]',
  productImageWrapper: '[data-product-image-wrapper]',
  productPrice: '[data-product-price]',
  productSaving: '[data-product-saving]',
  selectedOptions: '[data-selected-option]',
  bundleFormElement: '[data-bundle-form]',
  preorderDetails: '[data-preorder-details]',
  pairAndSaveSlider: '[data-pair-and-save-slider]',
  plattarArButton: '[data-plattar-ar]',
  preorderDate: '[data-preorder-date]',
  loyaltyBlock: '[data-loyalty-calculation]'
};

const cssClasses = {
  hide: 'hide',
  onsale: 'product__price--on-sale',
  hidden: 'hidden',
};

/**
 * Product section constructor. Runs on page load as well as Theme Editor
 * `section:load` events.
 * @param {string} container - selector for the section container DOM element
 */

export default register('product', {
  async onLoad() {
    // Register the product form using @shopify/theme-product-form
    this.formElement = this.container.querySelector(selectors.formElement);
    if (this.formElement) {
      this.productHandle = this.formElement.dataset.productHandle;
      const options = {
        onOptionChange: this.onOptionChange.bind(this),
        onFormSubmit: this.onFormSubmit.bind(this),
        // theme-product-form requires callbacks to be passed
        // for all four form events.
        onQuantityChange: () => true,
        onPropertyChange: () => true,
      };
      fetch(`/products/${this.productHandle}.js`)
        .then(response => response.json())
        .then(productJSON => {
          this.productForm = new ProductForm(this.formElement, productJSON, options);
          const addToCartBtn = this.container.querySelector(selectors.addToCart);
          if (addToCartBtn) {
            addToCartBtn.classList.remove(cssClasses.hidden);
          }
        });
    }

    // Initialise the bundle form if this is a bundle product page
    this.bundleFormElement = this.container.querySelector(selectors.bundleFormElement);
    if (this.bundleFormElement) {
      this.bundleFormElement.addEventListener('submit', this.onBundleFormSubmit.bind(this));
    }

    // Parse the settings string passed through from liquid
    const settingsData = this.container.dataset.sectionSettings || '{}';
    this.sectionSettings = settingsData.constructor === 'Object' && JSON.parse(settingsData);

    // If URL contains ?variant= param, get the selected variant ID
    const selectedVariant = this.container.dataset.selectedVariant;

    const externalVideos = this.container.querySelectorAll('[data-video-index] > iframe');
    if (externalVideos.length > 0) {
      let tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.async = 'async';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = function () {
        externalVideos.forEach(function (playerIframe) {
          const name = `player-${playerIframe.id}`;
          window[name] = new YT.Player(playerIframe.id);
        });
      };
    }

    // Video play buttons
    const playButtons = this.container.querySelectorAll('.product__play-button');
    playButtons.forEach(playButton => playButton.addEventListener('click', this.playPauseVideo.bind(this)));

    // Video mute buttons
    const muteButtons = this.container.querySelectorAll('.product__mute-button');
    muteButtons.forEach(muteButton => muteButton.addEventListener('click', this.muteUnmuteVideo.bind(this)));

    const modelViewers = this.container.querySelectorAll('model-viewer');
    if (modelViewers.length > 0) {
      const modelViewerVersion = modelViewers[0].dataset.version;
      const modelsData = this.container.querySelector(selectors.modelsJson)?.textContent;
      if (modelViewerVersion && modelsData) {
        const modelsDataJson = JSON.parse(modelsData);
        modelViewerLoader(modelViewerVersion, modelsDataJson)
          .then(() => null)
          .catch(error => window.console.log(error));
      }
    }

    const handlePlattarArButtonClick = event => {
      const embed = this.container.querySelector(`plattar-embed[scene-id="${event.currentTarget.dataset.plattarAr}"]`);
      if (!embed) return;

      if (!PlattarARAdapter.Util.canAugment()) {
        return embed.startQRCode();
      }

      event.preventDefault();
      event.stopPropagation();

      embed.startAR().catch(() => {
        embed.startQRCode();
      });
    };
    const plattarArButtons = this.container.querySelectorAll(selectors.plattarArButton);
    plattarArButtons.forEach(plattarArButton => plattarArButton.addEventListener('click', handlePlattarArButtonClick));

    // Look for a product image slideshow.
    this.slideShow = this.container.querySelector('.product__slideshow');

    // Initialise the slider
    this.slider = null;
    if (this.slideShow) {
      const settings = this.sectionSettings;
      const updateFeaturedImage = this.updateFeaturedImage.bind(this);
      this.slider = new Flickity(this.slideShow, {
        draggable: '>1',
        freeScroll: false,
        prevNextButtons: false,
        autoPlay: false,
        pageDots: settings.include_pager_dots,
        imagesLoaded: true,
        setGallerySize: true,
        cellAlign: 'center',
        cellSelector: '.product__media-item',
        watchCSS: true,
        wrapAround: settings.infinite,
        fullscreen: true,
        on: {
          ready: function () {
            // Stop slider drag events from impacting 3D model interaction
            const modelViewers = this.slider.querySelectorAll('model-viewer');
            if (modelViewers.length > 0) {
              const modelStopPropagation = event => event.stopPropagation();
              modelViewers.forEach(modelViewer => {
                modelViewer.addEventListener('mousedown', modelStopPropagation);
                modelViewer.addEventListener('touchstart', modelStopPropagation);
                modelViewer.addEventListener('pointerdown', modelStopPropagation);
              });
            }

            // Select the slider image matching the current variant
            updateFeaturedImage(selectedVariant ? selectedVariant : null, this);
          },
          change: function () {
            const videoMedia = this.slider.querySelectorAll('video');
            if (videoMedia.length > 0) {
              videoMedia.forEach(function (video) {
                video.pause();
              });
            }

            const externalVideos = this.slider.querySelectorAll('[data-video-index] > iframe');
            if (externalVideos.length > 0) {
              externalVideos.forEach(function (iframe) {
                const name = `player-${iframe.id}`;
                if (window[name] && typeof window[name].stopVideo === 'function') {
                  window[name].stopVideo();
                }
              });
            }
          },
          fullscreenChange: function (isFullscreen) {
            if (isFullscreen) {
              document.body.classList.add('flickity-is-fullscreen');
            } else {
              document.body.classList.remove('flickity-is-fullscreen');
            }
          },
        },
      });

      // On clicking a slider enter flickity fullscreen mode
      const slides = this.slideShow.querySelectorAll('.product__media-item');
      const dragSlides = this.slider;
      
      dragSlides.on('dragStart', () => {
        dragSlides.slider.childNodes.forEach(slide => {
          slide.style.pointerEvents = "none";
        });
      });
      
      dragSlides.on('dragEnd', () => {
        dragSlides.slider.childNodes.forEach(slide => {
          slide.style.pointerEvents = "all";
        });
      });

      slides.forEach(slide =>
        slide.addEventListener('click', () => {
          this.slider.toggleFullscreen();
        })
      );
    }

    // Look for a product thumbnail slideshow.
    this.thumbnailSlideshow = this.container.querySelector('.product__thumbnails');

    // Initialise the thumbnail slider
    this.thumbSlider = null;
    if (this.thumbnailSlideshow) {
      const container = this.container;
      const thumbSlider = container.querySelector('.product__thumbnails');
      this.thumbSlider = new Flickity(thumbSlider, {
        prevNextButtons: false,
        draggable: false,
        percentPosition: false,
        groupCells: true,
        cellAlign: 'left',
        pageDots: false,
        asNavFor: this.slideShow,
      });
    }

    // Initialise the product tabs
    const productTabsContainers = document.querySelectorAll('[data-product-tabs]');
    productTabsContainers.forEach(productTabsContainer =>
      productTabsContainer.addEventListener('click', this.onClickProductTabs)
    );

    /**
     * Initialise the review stars.
     */
    const reviewStars = this.container.querySelectorAll('.okeReviews-ratingWrapper');
    reviewStars.forEach(stars => {
      stars.addEventListener('click', () => {
        const reviewTabButton = document.querySelector('#product-reviews-tab');
        if (!reviewTabButton) return;

        reviewTabButton.click();

        window.scrollTo({
          top: reviewTabButton.offsetTop - 100,
          left: 0,
          behavior: 'smooth',
        });
      });
    });

    // Initialise the description read more
    const productDescriptionMoreButton = this.container.querySelector('.product-description__more-button');
    const productDescriptionMoreContent = this.container.querySelector('.product-description__more-content');
    if (productDescriptionMoreButton && productDescriptionMoreContent) {
      productDescriptionMoreButton.addEventListener('click', this.onClickDescriptionMoreButton.bind(this));
    }

    // Initialise the social icons
    const socialSharingToggle = this.container.querySelector('.product__social-sharing-toggle');
    if (socialSharingToggle) {
      socialSharingToggle.addEventListener('click', this.onClickSocialSharingToggle.bind(this));
    }

    const recentlyViewedProductsTab = this.container.querySelector('#recently-viewed-tab');
    if (recentlyViewedProductsTab) {
      recentlyViewedProducts();
    }

    // Initialise pair and saves
    const pairAndSaveCTAs = this.container.querySelectorAll(selectors.addPairAndSave);
    pairAndSaveCTAs.forEach(pairAndSaveCTA => {
      pairAndSaveCTA.addEventListener('click', () => {
        const variantId = pairAndSaveCTA.dataset.variantId;

        if (!variantId) return;

        window.sideCart.handleAddToCartEvent(
          [
            {
              id: variantId,
              quantity: 1,
            },
          ],
          true
        );
      });
    });

    const pairAndSaveSlider = this.container.querySelector(selectors.pairAndSaveSlider);
    if (pairAndSaveSlider) {
      new Flickity(pairAndSaveSlider, {
        prevNextButtons: true,
        pageDots: true,
        cellAlign: 'left',
        arrowShape:
          'M75.8703 1.12651C74.364 -0.375504 71.9218 -0.375504 70.4154 1.12651L24.1297 47.2804C22.6234 48.7824 22.6234 51.2176 24.1297 52.7196L70.4154 98.8735C71.9218 100.376 74.364 100.376 75.8703 98.8735C77.3766 97.3715 77.3766 94.9362 75.8703 93.4342L32.312 50L75.8703 6.56579C77.3766 5.06378 77.3766 2.62853 75.8703 1.12651Z',
      });
    }

    // Initialise payment icons show / hide
    const paymentIconsToggles = this.container.querySelectorAll('a[href="#product-payment-icons"]');
    paymentIconsToggles.forEach(paymentIconsToggle =>
      paymentIconsToggle.addEventListener('click', event => {
        event.preventDefault();
        const paymentIcons = this.container.querySelector(event.target.hash);
        if (!paymentIcons) {
          return;
        }
        paymentIcons.classList.toggle('hide');
      })
    );

    // Initialise payment options modal (only used on the product.featured-1 etc. templates)
    this.initPaymentOptionsModal();

    if (document.querySelector('body').classList.contains('template-product')) {
      const variant = JSON.parse(currentVariant)
      const bundleTags = productTags.filter(x => x.includes("bundle")); // Get bundle tags


      if (bundleTags.length == 0) {
        // Update Add to cart button state on load
        if (!variant) {
          this.updateAddToCartState(null, variant);
          this.updateAddToCartStickyState(null, variant);
          return;
        }
        if (!variant.available) {

          this.updateAddToCartState(false, variant);
          this.updateAddToCartStickyState(false, variant);
          return;
        }
        this.updateAddToCartState(true, variant);
        this.updateAddToCartStickyState(true, variant);
      }
    }
  },

  // Event callback for option/variant switch event
  onOptionChange(event) {
    const variant = event.dataset.variant;

    if (!variant) {
      // The combination of selected options does not have a matching variant
      this.updateAddToCartState(null, variant);
      this.updateAddToCartStickyState(null, variant);
      this.updateProductPrices(false);
      this.updateFeaturedImage(false);
      this.updateSelectedOption(false);
      return;
    }

    // Check for a discount on the window.theme object
    const price = window.theme.discount > 0 ? ((100 - window.theme.discount) * variant.price) / 100 : variant.price;
    const compareAtPrice = window.theme.discount > 0 ? variant.price : variant.compare_at_price;

    this.handleLoyaltyPoints(price)

    if (!variant.available) {
      // The combination of selected options has a matching variant but it is
      // currently unavailable
      this.updateAddToCartState(false, variant);
      this.updateAddToCartStickyState(false, variant);
      this.updateProductPrices(true, price, compareAtPrice);
      this.updateFeaturedImage(variant.id);
      this.updateSelectedOption(variant);
      return;
    }

    // The combination of selected options has a matching variant and it is
    // available
    this.updateAddToCartState(true, variant);
    this.updateAddToCartStickyState(true, variant);
    this.updateProductPrices(true, price, compareAtPrice);
    this.updateFeaturedImage(variant.id);
    this.updateSelectedOption(variant);
  },
  handleLoyaltyPoints(price) {
    const message = this.container.querySelector('.loyalty-points .rewarded-points');
    if (!message) {
      return
    }
    const priceString = (price/100).toFixed(2);
    const multiplerPoints = Number(message.getAttribute('data-multiplier-lpoint'));
    const bonusPoints = Number(message.getAttribute('data-bonus-lpoints'));
    let premessage = '';

    // Update total earning when variant changed
    const totalPointsWithMultipler = Number((price / 100) * multiplerPoints) - (price/100);
    const totalPointsWithBonus = bonusPoints;

    if (multiplerPoints >= 3) {
      premessage = window.mymix.triple_point_message;
    } else if (multiplerPoints >= 2) {
      premessage = window.mymix.double_point_message;
    }

    if (totalPointsWithMultipler >= totalPointsWithBonus) {
      message.innerHTML = `${premessage} Earn ${priceString} points for this item${totalPointsWithMultipler > 0 ? ', plus ' + totalPointsWithMultipler.toFixed(2) + ' Bonus Points!' : '!'}`;
    } else {
      message.innerHTML = `${premessage} Earn ${priceString} points for this item${totalPointsWithBonus > 0 ? ', plus ' + totalPointsWithBonus + ' Bonus Points!' : '!'}`;
    }
  },

  // Event callback for Theme Editor `section:unload` event
  onUnload() {
    this.productForm.destroy();
  },

  // Event callback for product form submit event
  onFormSubmit(event) {
    event.preventDefault();

    /**
     * If there is a max:x tag on the product, we output a hidden max quantity input.
     * This is passed along with the handle to check the quantity doesn't exceed the
     * max allowed quantity in the cart.
     */
    const maxQuantityEl = this.formElement.querySelector('[data-max-quantity]');
    let maxQuantity = 0;
    if (maxQuantityEl) {
      maxQuantity = maxQuantityEl.value;
    }
    const variant = event.dataset.variant;
    const quantity = event.dataset.quantity;
    if (variant && variant.id && quantity) {
      const properties = Object.assign({}, event.dataset.properties, {
        _original_price: variant.price,
      });
      const items = [
        {
          quantity,
          id: variant.id,
          properties,
        },
      ];
      window.sideCart.handleAddToCartEvent(items, true, maxQuantity, this.productHandle);
    }
  },

  // Event callback for bundle form submit event
  onBundleFormSubmit(event) {
    event.preventDefault();

    /**
     * Extract items from submitted form fields.
     * These come through in an object form so we have to convert
     * to an array using Object.values before sending to the
     * handleAddToCartEvent function.
     */
    const { items } = FormDataJson.formToJson(event.target);

    /**
     * Add a timestamp to the items to keep each bundle in the cart unique
     */
    const timestamp = Date.now().toString();
    const bundleItems = Object.values(items)
      .filter(item => item.id) // Only include items with a valid id (required by Shopify)
      .map(item => ({
        ...item,
        properties: {
          ...(item.properties || {}),
          _bundle: `${item.properties?._bundle || ''}${timestamp}`,
          _bundle_identifier: timestamp,
        },
      }));

    const expectedBundleItems = Number(bundleItems[0]?.properties?._bundle_items);
    const bundleIsMissingChildren = bundleItems.length !== expectedBundleItems;

    if (bundleIsMissingChildren) {
      window.console.error('Bundle add blocked: a bundle item is missing its variant id.');
      return;
    }

    window.sideCart.handleAddToCartEvent(bundleItems, true);
  },

  /**
   * Updates the DOM state of the add to cart button
   *
   * @param {boolean} enabledState - True is enabled. False is disabled. Null is disabled without prices.
   * @param {string} priceWrapper - DOM selector for prices container div
   * @param {string} button - DOM selector for add to cart button
   */
  updateAddToCartState(enabledState, selectedVariant) {
    const preorder = this.container.dataset.preorder === 'true';
    const addToCartBtn = this.container.querySelector(selectors.addToCart);
    const addToCartBtnSticky = document.querySelector(selectors.addToCartSticky);
    const backInStockBtn = this.container.querySelector(selectors.backInStock);
    const preorderDetails = this.container.querySelector(selectors.preorderDetails);
    const priceWrapper = this.container.querySelector(selectors.priceWrapper);
    let preorderDate = this.container.querySelector(selectors.preorderDate)
    const preorderVariants = productTags.filter(x => x.includes("pre-orderVariant")); // Get enabled preorder in variant-level
    const variant = (obj) => obj.split(':')[1] == selectedVariant.title;
    var element = document.querySelector('[data-countdown] .countdown-to-show');
    if (element) {
      var isVisible = window.getComputedStyle(element).getPropertyValue('display') !== 'none';
      if (isVisible) {
        enabledState = false;
      }
    }
    switch (enabledState) {
      // Variant is available
      case true: {
        priceWrapper.classList.remove(cssClasses.hide);
        addToCartBtn.removeAttribute('disabled');
        if (preorder) {

          // Determine if a variant is preorder enabled
          if (preorderVariants.some(variant) && selectedVariant.title != 'Default Title') {
            addToCartBtn.classList.remove('btn--secondary');
            addToCartBtn.classList.add('btn--quarternary');
            addToCartBtn.textContent = theme.strings.preorder;
            preorderDate.value = preorderDate.dataset.preorderDate
            preorderDetails.style.display = 'block';
          } else if (selectedVariant.title == 'Default Title') {
            addToCartBtn.classList.remove('btn--secondary');
            addToCartBtn.classList.add('btn--quarternary');
            addToCartBtn.textContent = theme.strings.preorder;
            preorderDate.value = preorderDate.dataset.preorderDate
            preorderDetails.style.display = 'block';
          } else {
            addToCartBtn.classList.remove('btn--secondary');
            addToCartBtn.classList.remove('btn--quarternary');
            addToCartBtn.textContent = theme.strings.addToCart;
            preorderDate.value = ''
            preorderDetails.style.display = 'none';
          }
        } else {
          addToCartBtn.classList.remove('btn--quarternary');
          addToCartBtn.textContent = theme.strings.addToCart;
        }
        if (backInStockBtn) {
          setTimeout(() => (backInStockBtn.style.display = 'none'), 200);
        } else {
          console.log('no back in stock button');
        }
        break;
      }
      // Variant is unavailable
      case false: {
        priceWrapper.classList.remove(cssClasses.hide);
        addToCartBtn.setAttribute('disabled', '');
        addToCartBtn.classList.add('btn--secondary');
        addToCartBtn.classList.remove('btn--quarternary');
        addToCartBtn.textContent = theme.strings.soldOut;
        if (preorderDetails) preorderDetails.style.display = 'none';
        setTimeout(() => (backInStockBtn.style.display = 'block'), 200);
        break;
      }
      // No matching variant
      case null: {
        priceWrapper.classList.add(cssClasses.hide);
        addToCartBtn.setAttribute('disabled', '');
        addToCartBtn.classList.add('btn--secondary');
        addToCartBtn.classList.remove('btn--quarternary');
        addToCartBtn.textContent = theme.strings.unavailable;
        if (preorderDetails) preorderDetails.style.display = 'none';
        setTimeout(() => (backInStockBtn.style.display = 'block'), 200);
        break;
      }
    }
  },

  updateAddToCartStickyState(enabledState, selectedVariant) {
    const addToCartBtn = document.querySelector(selectors.addToCartSticky);

    if (!addToCartBtn) {
      return false;
    }

    const preorder = this.container.dataset.preorder === 'true';
    const backInStockBtn = this.container.querySelector(selectors.backInStock);
    const preorderDetails = this.container.querySelector(selectors.preorderDetails);
    const priceWrapper = this.container.querySelector(selectors.priceWrapper);
    let preorderDate = this.container.querySelector(selectors.preorderDate)
    const preorderVariants = productTags.filter(x => x.includes("pre-orderVariant")); // Get enabled preorder in variant-level
    const variant = (obj) => obj.split(':')[1] == selectedVariant.title

    switch (enabledState) {
      // Variant is available
      case true: {
        priceWrapper.classList.remove(cssClasses.hide);
        addToCartBtn.removeAttribute('disabled');
        if (preorder) {

          // Determine if a variant is preorder enabled
          if (preorderVariants.some(variant) && selectedVariant.title != 'Default Title') {
            addToCartBtn.classList.remove('btn--secondary');
            addToCartBtn.classList.add('btn--quarternary');
            addToCartBtn.textContent = theme.strings.preorder;
            preorderDate.value = preorderDate.dataset.preorderDate
            preorderDetails.style.display = 'block';
          } else if (selectedVariant.title == 'Default Title') {
            addToCartBtn.classList.remove('btn--secondary');
            addToCartBtn.classList.add('btn--quarternary');
            addToCartBtn.textContent = theme.strings.preorder;
            preorderDate.value = preorderDate.dataset.preorderDate
            preorderDetails.style.display = 'block';
          } else {
            addToCartBtn.classList.remove('btn--secondary');
            addToCartBtn.classList.remove('btn--quarternary');
            addToCartBtn.textContent = theme.strings.addToCart;
            preorderDate.value = ''
            preorderDetails.style.display = 'none';
          }
        } else {
          addToCartBtn.classList.remove('btn--quarternary');
          addToCartBtn.textContent = theme.strings.addToCart;
        }

        setTimeout(() => (backInStockBtn.style.display = 'none'), 200);
        break;
      }
      // Variant is unavailable
      case false: {
        priceWrapper.classList.remove(cssClasses.hide);
        addToCartBtn.setAttribute('disabled', '');
        addToCartBtn.classList.add('btn--secondary');
        addToCartBtn.classList.remove('btn--quarternary');
        addToCartBtn.textContent = theme.strings.soldOut;
        if (preorderDetails) preorderDetails.style.display = 'none';
        setTimeout(() => (backInStockBtn.style.display = 'block'), 200);
        break;
      }
      // No matching variant
      case null: {
        priceWrapper.classList.add(cssClasses.hide);
        addToCartBtn.setAttribute('disabled', '');
        addToCartBtn.classList.add('btn--secondary');
        addToCartBtn.classList.remove('btn--quarternary');
        addToCartBtn.textContent = theme.strings.unavailable;
        if (preorderDetails) preorderDetails.style.display = 'none';
        setTimeout(() => (backInStockBtn.style.display = 'block'), 200);
        break;
      }
    }
  },

  /**
   * Updates the DOM with specified prices
   *
   * @param {boolean} variantExists - Selected options match a valid variant
   * @param {number} price - Selected variant price
   * @param {number} compareAtPrice - Selected variant original price
   * @param {string} productPrice - DOM selector for current price of the product
   * @param {string} comparePrice - DOM selector for original price of the product
   * @param {string} priceWrapper - DOM selector for prices container div
   */
  updateProductPrices(variantExists, price, compareAtPrice) {
    const comparePrice = this.container.querySelector(selectors.comparePrice);
    const productSaving = this.container.querySelector(selectors.productSaving);
    const productPrice = this.container.querySelector(selectors.productPrice);
    const priceWrapper = this.container.querySelector(selectors.priceWrapper);

    // No matching variant
    if (!variantExists) {
      comparePrice.textContent = '';
      comparePrice.classList.add(cssClasses.hide);
      productSaving.textContent = '';
      productSaving.classList.add(cssClasses.hide);
      priceWrapper.classList.remove(cssClasses.onsale);
      return;
    }

    // Variant exists
    productPrice.textContent = formatMoney(price, theme.moneyFormat);

    if (price < compareAtPrice) {
      comparePrice.textContent = formatMoney(compareAtPrice, theme.moneyFormat);
      comparePrice.classList.remove(cssClasses.hide);
      productSaving.textContent = window.theme.strings.saving.replace(
        '{{ price }}',
        formatMoney(compareAtPrice - price, theme.moneyFormat)
      );
      productSaving.classList.remove(cssClasses.hide);
      priceWrapper.classList.add(cssClasses.onsale);
    } else {
      comparePrice.textContent = '';
      comparePrice.classList.add(cssClasses.hide);
      productSaving.textContent = '';
      productSaving.classList.add(cssClasses.hide);
      priceWrapper.classList.remove(cssClasses.onsale);
    }
  },

  /**
   * Scrolls the image slider to that of the current variant
   *
   * @param {number} variantId - ID of the selected variant
   * @param {string} sliderImages - DOM selector for product image slider
   */
  updateFeaturedImage(variantId, thisSlider) {
    const id = variantId ? variantId : this.container.dataset.firstVariant;
    const slider = thisSlider ? thisSlider : this.slider;

    const sliderImages = this.container.querySelectorAll(selectors.productImageWrapper);
    if (sliderImages.length > 0) {
      for (let i = 0; i < sliderImages.length; i++) {
        const slide = sliderImages[i];
        const isVariantImage = slide.dataset.imageVariants.includes(id);

        if (isVariantImage && slider) {
          slider.select(Number(slide.dataset.slideIndex), this.sectionSettings.infinite, false);
          break;
        }
      }
    }
  },

  /**
   * Updates the label of the selected option for colour swatches
   *
   * @param {object} variant - the selected variant object
   */
  updateSelectedOption(variant) {
    if (!variant) {
      return;
    }

    const $selectedOptions = this.container.querySelectorAll(selectors.selectedOptions);
    if ($selectedOptions.length < 1) {
      return;
    }

    $selectedOptions.forEach($selectedOption => {
      const selectedValue = variant[$selectedOption.dataset.selectedOption];
      $selectedOption.innerHTML = selectedValue;
    });
  },

  /**
   * Handle product tab clicks
   */
  onClickProductTabs(event) {
    const tabs = event.currentTarget;
    const tabsID = tabs.dataset.productTabs;
    const tab = event.target.closest(`[data-product-tab="${tabsID}"]`);
    if (!tab) {
      return;
    }
    const tabPanel = tabs.querySelector(`#${tab.getAttribute('aria-controls')}`);
    if (!tabPanel) {
      return;
    }

    // If clicked tab is already active, remove active state on mobile only and only if tabsID is not set
    if (tab.classList.contains('active') && window.innerWidth < window.theme.breakpoints.large && !tabsID) {
      tab.classList.remove('active');
      tab.removeAttribute('aria-selected');
      tabPanel.classList.remove('active');
      return;
    }

    // Remove all active tab classes
    const allTabs = tabs.querySelectorAll(`[data-product-tab="${tabsID}"]`);
    allTabs.forEach(tab => {
      tab.classList.remove('active');
      tab.removeAttribute('aria-selected');
    });
    const allTabPanels = tabs.querySelectorAll(`[data-product-tab-panel="${tabsID}"]`);
    allTabPanels.forEach(tabPanel => {
      tabPanel.classList.remove('active');
    });

    // Set clicked tab to active
    tab.classList.add('active');
    tab.setAttribute('aria-selected', 'true');
    tabPanel.classList.add('active');

    // Resize any sliders in the tab content
    const sliders = tabPanel.querySelectorAll('.flickity-enabled');
    sliders.forEach(slider => Flickity.data(slider).resize());

    // Initialise any Plattar models in the tab content
    const plattarSceneId = tabPanel.querySelector('[data-plattar-scene-id]');
    if (plattarSceneId) {
      plattarSceneId.innerHTML = `<plattar-viewer scene-id="${plattarSceneId.dataset.plattarSceneId}" width="100%" height="100%"></plattar-viewer>`;
      delete plattarSceneId.dataset.plattarSceneId;
    }

    // Scroll the user to the top if tabsID === 'product-featured'
    if (tabsID === 'product-featured') {
      window.scrollTo({ top: 0 });
    }
  },

  /**
   * Handle description read more clicks
   */
  onClickDescriptionMoreButton(event) {
    const readMoreButton = event.currentTarget;
    const readMoreContent = this.container.querySelector('.product-description__more-content');
    if (!readMoreContent) {
      return;
    }

    /**
     * By removing focus from the read more button before showing the more content
     * we stop the browser from keeping the read more button on screen.
     */
    readMoreButton.blur();

    if (readMoreContent.classList.contains('active')) {
      readMoreContent.classList.remove('active');
      readMoreButton.innerHTML = window.theme.strings.productDescriptionMore;
    } else {
      readMoreContent.classList.add('active');
      readMoreButton.innerHTML = window.theme.strings.productDescriptionLess;
    }
  },

  /**
   * Handle social sharing toggle clicks
   */
  onClickSocialSharingToggle() {
    const socialSharing = this.container.querySelector('.product__social-sharing');
    if (!socialSharing) {
      return;
    }
    socialSharing.classList.toggle('active');
  },

  /**
   * Initialise the payment options modal
   */
  initPaymentOptionsModal() {
    const paymentOptionsModal = this.container.querySelector('.modal--payment-options');
    if (!paymentOptionsModal) {
      return;
    }

    this.productInclusionsModal = new Dialog('.modal--payment-options', {
      openingSelector: '.modal-payment-options-open',
      closingSelector: '.modal-payment-options-close',
      backdropSelector: '.overlay--payment-options',
      onOpen: function () {
        document.body.classList.add('modal--payment-options-open');
      },
      onClose: function () {
        document.body.classList.remove('modal--payment-options-open');
      },
    });

    const paymentOptionModalTriggers = this.container.querySelectorAll('[data-open-payment-option-modal]');
    paymentOptionModalTriggers.forEach(paymentOptionModalTrigger =>
      paymentOptionModalTrigger.addEventListener('click', this.openPaymentOptionsModal.bind(this))
    );
  },

  /**
   * Handle opening payment options modal
   */
  openPaymentOptionsModal() {
    this.productInclusionsModal.open();
  },

  /**
   * Handle play/pause on media with play button
   */
  playPauseVideo(event) {
    const playButton = event.currentTarget;
    const video = playButton.previousElementSibling;
    if (!video) {
      return;
    }

    if (video.paused) {
      video.play();
      playButton.classList.add('is-playing');
    } else {
      video.pause();
      playButton.classList.remove('is-playing');
    }
  },

  /**
   * Handle mute/unmute on media with mute button
   */
  muteUnmuteVideo(event) {
    const muteButton = event.currentTarget;
    const video = muteButton.previousElementSibling.previousElementSibling;
    if (!video) {
      return;
    }

    if (video.muted) {
      video.muted = false;
      muteButton.classList.remove('is-muted');
    } else {
      video.muted = true;
      muteButton.classList.add('is-muted');
    }
  },
});
